import React from 'react';
import FormField from '../shared/FormField';
import GenderSelect from '../shared/GenderSelect';
import MaritalStatusSelect from '../shared/MaritalStatusSelect';
import CountrySelect from '../shared/CountrySelect';
import ProvinceCitySelect from '../shared/ProvinceCitySelect';
import formDataStorage from '../../utils/formDataStorage';
import { Plus, Trash2, Calendar, ChevronDown } from 'lucide-react';
import telecode from 'chinese-telecode'
import { pinyin } from 'pinyin-pro'



const convertNameToTelecode = (chineseName) => {
  /*
  姓名转换为电话码
  @param {string} chineseName - 中文姓名
  @returns {string} - 电话码
  */
  const codes = telecode(chineseName)
  return codes.join(' ')
};

// 个人信息1表单
const Personal1Form = ({ data, onChange }) => {

  // 从formDataStorage读取数据并确保数据同步
  React.useEffect(() => {
    const savedData = formDataStorage.loadFormData('personal_1');
    if (savedData && Object.keys(savedData).length > 0) {
      Object.keys(savedData).forEach(key => {
        onChange(key, savedData[key]);
      });
    }
  }, [onChange]);

  // 月份映射
  const MONTHS = [
    { value: 'JAN', label: '01月' },
    { value: 'FEB', label: '02月' },
    { value: 'MAR', label: '03月' },
    { value: 'APR', label: '04月' },
    { value: 'MAY', label: '05月' },
    { value: 'JUN', label: '06月' },
    { value: 'JUL', label: '07月' },
    { value: 'AUG', label: '08月' },
    { value: 'SEP', label: '09月' },
    { value: 'OCT', label: '10月' },
    { value: 'NOV', label: '11月' },
    { value: 'DEC', label: '12月' }
  ];

  // 获取指定年月的天数
  const getDaysInMonth = (year, month) => {
    if (!year || !month) return 31;
    const monthIndex = MONTHS.findIndex(m => m.value === month) + 1;
    return new Date(year, monthIndex, 0).getDate();
  };

  // 生成日期选项
  const generateDayOptions = () => {
    const birthData = data.date_and_place_of_birth || {};
    const year = birthData.year;
    const month = birthData.month;
    const maxDays = getDaysInMonth(year, month);

    return Array.from({ length: maxDays }, (_, i) => {
      const day = String(i + 1).padStart(2, '0');
      return { value: day, label: `${day}日` };
    });
  };

  // 验证出生日期是否有效（不能是今天或之后）
  const validateBirthDate = (year, month, day) => {
    if (!year || !month || !day) return '';

    const birthDate = new Date(year, MONTHS.findIndex(m => m.value === month), parseInt(day));
    const today = new Date();
    today.setHours(0, 0, 0, 0); // 重置时间为当天开始

    if (birthDate >= today) {
      return '出生日期不能是今天或之后的日期';
    }

    return '';
  };

  // 更新出生日期和地点
  const updateBirthData = (field, value) => {
    const currentBirthData = data.date_and_place_of_birth || {};
    const newBirthData = {
      ...currentBirthData,
      [field]: value,
    };
    onChange('date_and_place_of_birth', newBirthData);
  };
  const updateProvince = (value) => {
    const currentBirthData = data.date_and_place_of_birth || {};
    const newBirthData = {
      ...currentBirthData,
      'state_or_province': value,
    };
    onChange('date_and_place_of_birth', newBirthData)
  };
  const updateCity = (value) => {
    const currentBirthData = data.date_and_place_of_birth || {};
    const newBirthData = {
      ...currentBirthData,
      'city': value,
    };
    onChange('date_and_place_of_birth', newBirthData)
  };

  // 添加别名
  const addOtherName = () => {
    onChange('has_other_name', true);
    const currentOtherNames = data.other_names || [];
    const newOtherNames = [...currentOtherNames, { surnames: '', given_name: '' }];
    onChange('other_names', newOtherNames);
  };

  // 删除别名
  const removeOtherName = (index) => {
    const currentOtherNames = data.other_names || [];
    const newOtherNames = currentOtherNames.filter((_, i) => i !== index);
    onChange('other_names', newOtherNames);
    if (newOtherNames.length === 0) {
      onChange('has_other_name', false);
    }
  };

  // 更新别名
  const updateOtherName = (index, field, value) => {
    const currentOtherNames = data.other_names || [];
    const newOtherNames = [...currentOtherNames];
    if (!newOtherNames[index]) {
      newOtherNames[index] = { surnames: '', given_name: '' };
    }
    newOtherNames[index] = { ...newOtherNames[index], [field]: value };
    onChange('other_names', newOtherNames);
  };

  // 更新电码名字
  const updateTelecodeName = (field, value) => {
    const currentTelecode = data.telecode_name || {};
    onChange('telecode_name', { ...currentTelecode, [field]: value });
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg">
        <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">1</span>
          个人基本信息
        </h3>

        {/* 基本姓名信息 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">基本姓名信息</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              label="姓氏（中文填写，自动转化成拼音）"
              value={data.surnames || ''}
              onChange={(e) => {
                // 保存中文原始值
                const newSurnames = e.target.value;
                onChange('surnames_cn', newSurnames);
                onChange('surnames', newSurnames); // 临时保存，等待onBlur转为拼音
                onChange('has_telecode_name', true);
                updateTelecodeName('surnames', convertNameToTelecode(newSurnames));

                // 自动拼接到full_name（使用中文原始值：姓_cn+名_cn）
                const givenNameCn = data.given_name_cn || '';
                if (givenNameCn) {
                  onChange('full_name', newSurnames + givenNameCn);
                }
              }}
              onBlur={(e) => {
                // 存储拼音值到surnames字段
                const pinyinSurnames = pinyin(e.target.value, { toneType: "none" }).replace(/\s+/g, '');
                onChange('surnames', pinyinSurnames);
              }}
              placeholder="张"
              required
            />
            <FormField
              label="名字（中文填写，自动转化成拼音）"
              value={data.given_name || ''}
              onChange={(e) => {
                // 保存中文原始值
                const newGivenName = e.target.value;
                onChange('given_name_cn', newGivenName);
                onChange('given_name', newGivenName); // 临时保存，等待onBlur转为拼音
                onChange('has_telecode_name', true);
                updateTelecodeName('given_name', convertNameToTelecode(newGivenName));

                // 自动拼接到full_name（使用中文原始值：姓_cn+名_cn）
                const surnamesCn = data.surnames_cn || '';
                if (surnamesCn) {
                  onChange('full_name', surnamesCn + newGivenName);
                }
              }}
              onBlur={(e) => {
                // 存储拼音值到given_name字段
                const pinyinGivenName = pinyin(e.target.value, { toneType: "none" }).replace(/\s+/g, '');
                onChange('given_name', pinyinGivenName);
              }}
              placeholder="三"
              required
            />
            <FormField
              label="中文全名（根据姓名自动合并）"
              value={data.full_name || ''}
              onChange={(e) => onChange('full_name', e.target.value)}
              placeholder="张三"
              required
              readOnly={true}
              disabled={!data.surnames || !data.given_name}
            />
          </div>
        </div>

        {/* 别名管理 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">您是否曾经用过其它名字? (例如：婚前用名、宗教用名、职业用名、别名等)</h4>

            <div>
              <div className="flex gap-6 mb-4">
                <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                  <input
                    type="radio"
                    name="has_other_name"
                    value="false"
                    checked={data.has_other_name === false}
                    onChange={(e) => {
                      onChange('has_other_name', false);
                      onChange('other_names', []);
                    }}
                    className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                  />
                  <span className="text-base font-medium text-gray-700">❌ 否 - 我从未使用过其他名字</span>
                </label>

                <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                  <input
                    type="radio"
                    name="has_other_name"
                    value="true"
                    checked={data.has_other_name === true}
                    onChange={(e) => {
                      onChange('has_other_name', true);
                      if (!data.other_names || data.other_names.length === 0) {
                        addOtherName();
                      }
                    }}
                    className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                  />
                  <span className="text-base font-medium text-gray-700">✅ 是 - 我曾经使用过其他名字</span>
                </label>
              </div>

              {data.has_other_name && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={addOtherName}
                    className="flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    添加别名
                  </button>
                </div>
              )}
            </div>
          </div>

          {data.has_other_name && (
            <div className="space-y-4">
              {/* 显示别名列表 */}
              <div className="space-y-3">
                {(() => {
                  // 如果 other_names 为空数组，则插入默认数据
                  const otherNames = data.other_names || [];

                  return otherNames.map((otherName, index) => (
                    <div key={index} className="flex items-center gap-3 p-4 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg border-l-4 border-blue-400 shadow-sm">
                      <div className="flex-1 grid grid-cols-2 gap-3">
                        <FormField
                          label={`别名${index + 1} - 姓氏`}
                          value={otherName.surnames || ''}
                          onChange={(e) => updateOtherName(index, 'surnames', e.target.value)}
                          placeholder="姓氏（英文）"
                          required
                        />
                        <FormField
                          label={`别名${index + 1} - 名字`}
                          value={otherName.given_name || ''}
                          onChange={(e) => updateOtherName(index, 'given_name', e.target.value)}
                          placeholder="名字（英文）"
                          required
                        />
                      </div>
                      {otherNames.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeOtherName(index)}
                          className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md mt-6"
                          title="删除此别名"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}
        </div>

        {/* 电码名字 - 隐藏界面，自动生成 */}

        {/* 基本信息 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">基本信息</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <GenderSelect
              label="性别"
              value={data.sex || ''}
              onChange={(value) => onChange('sex', value)}
              required
            />
            <MaritalStatusSelect
              label="婚姻状况"
              value={data.marital_status || ''}
              onChange={(value) => onChange('marital_status', value)}
              required
            />
          </div>
        </div>

        {/* 出生日期和地点 */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <Calendar className="w-5 h-5 mr-2" />
            出生日期和地点
          </h4>

          {/* 出生日期 */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              出生日期 <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-4">
              {/* 年份 */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2">年份</label>
                <input
                  type="number"
                  min="1900"
                  max={new Date().getFullYear()}
                  placeholder="2000"
                  value={(data.date_and_place_of_birth && data.date_and_place_of_birth.year) || ''}
                  onChange={(e) => updateBirthData('year', parseInt(e.target.value) || '')}
                  className="w-full h-12 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center bg-gradient-to-r from-white to-blue-50 shadow-sm hover:shadow-md transition-all duration-200 font-medium"
                />
              </div>

              {/* 月份 */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2">月份</label>
                <div className="relative">
                  <select
                    value={(data.date_and_place_of_birth && data.date_and_place_of_birth.month) || ''}
                    onChange={(e) => updateBirthData('month', e.target.value)}
                    className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gradient-to-r from-white to-blue-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium"
                  >
                    <option value="" className="text-gray-500">选择月份</option>
                    {MONTHS.map(month => (
                      <option key={month.value} value={month.value} className="text-gray-800 py-2">
                        {month.label}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>

              {/* 日期 */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2">日期</label>
                <div className="relative">
                  <select
                    value={(data.date_and_place_of_birth && data.date_and_place_of_birth.day) || ''}
                    onChange={(e) => updateBirthData('day', e.target.value)}
                    disabled={!data.date_and_place_of_birth?.year || !data.date_and_place_of_birth?.month}
                    className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gradient-to-r from-white to-blue-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed disabled:shadow-none"
                  >
                    <option value="" className="text-gray-500">选择日期</option>
                    {generateDayOptions().map(day => (
                      <option key={day.value} value={day.value} className="text-gray-800 py-2">
                        {day.label}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>

            {/* 出生日期验证错误提示 */}
            {(() => {
              const birthData = data.date_and_place_of_birth || {};
              const validationError = validateBirthDate(birthData.year, birthData.month, birthData.day);
              if (validationError) {
                return (
                  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex items-start">
                      <div className="w-5 h-5 text-red-600 mt-0.5 mr-2">⚠️</div>
                      <p className="text-sm text-red-800 font-medium">{validationError}</p>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </div>

          {/* 出生地点 */}
          <div className="space-y-4">
            {/* 省份城市选择 */}
            <ProvinceCitySelect
              provinceLabel="出生省份"
              cityLabel="出生地区"
              provinceValue={data.date_and_place_of_birth?.state_or_province || ''}
              cityValue={data.date_and_place_of_birth?.city || ''}
              onProvinceChange={(value) => updateProvince(value)}
              onCityChange={(value) => updateCity(value)}
              required
              className="mb-4"
            />

            {/* 出生国家 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <CountrySelect
                label="出生国家/地区"
                value={(data.date_and_place_of_birth && data.date_and_place_of_birth.country) || ''}
                onChange={(value) => updateBirthData('country', value)}
                required
              />
            </div>
          </div>
        </div>

        {/* 预览信息 */}
        {(data.surnames || data.given_name) && (
          <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-400 shadow-sm">
            <h4 className="font-semibold text-blue-800 mb-2">信息预览</h4>
            <div className="text-sm text-blue-700 space-y-1">
              <p><strong>英文姓名:</strong> {data.surnames} {data.given_name}</p>
              {data.full_name && <p><strong>中文全名:</strong> {data.full_name}</p>}
              {data.has_other_name && data.other_names && data.other_names.length > 0 && (
                <p><strong>别名数量:</strong> {data.other_names.length} 个</p>
              )}
              {data.has_telecode_name && data.telecode_name && (
                <p><strong>电码名字:</strong> {data.telecode_name.surnames} {data.telecode_name.given_name}</p>
              )}
              {data.sex && <p><strong>性别:</strong> {data.sex === 'M' ? '男性' : '女性'}</p>}
              {data.marital_status && (
                <p><strong>婚姻状况:</strong> {
                  {
                    'M': '已婚',
                    'C': '事实婚姻',
                    'P': '民事结合/家庭伙伴关系',
                    'S': '单身',
                    'W': '丧偶',
                    'D': '离婚',
                    'L': '法律分居',
                    'O': '其他'
                  }[data.marital_status] || data.marital_status
                }</p>
              )}
              {data.date_and_place_of_birth && data.date_and_place_of_birth.year && (
                <p><strong>出生日期:</strong>
                  {data.date_and_place_of_birth.year}-{data.date_and_place_of_birth.month}-{data.date_and_place_of_birth.day}
                  {(() => {
                    const birthData = data.date_and_place_of_birth;
                    const validationError = validateBirthDate(birthData.year, birthData.month, birthData.day);
                    if (validationError) {
                      return <span className="text-red-600 ml-2">❌ 日期无效</span>;
                    } else if (birthData.year && birthData.month && birthData.day) {
                      return <span className="text-green-600 ml-2">✅ 日期有效</span>;
                    }
                    return null;
                  })()}
                </p>
              )}

            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Personal1Form;
